import { parseFQTNC } from "../../../utils/utils";

export const Translations = {
    threshold_value: "Threshold",
    noise_mechanism: "Noise Mechanism",
    privacy_budget: "Privacy Budget",
    training_model: "Training model",
    XGBoost: "XG Boost",
    time_series_prediction_model: "Time-series prediction model",
    linear: "Linear Regression",
    identity_order: "Prioritized identity providers",
    dedup_conversions_checked: "Dedupe Conversions",
    audience_lookalike_modeling_columns: "Features",
    inventory_forecasting_and_planning_columns: "Features",
    reach_and_frequency_columns: "Campaign Mapping Details",
    allow_export_outside: "Allow exporting model(s)",

    TIMESTAMP: "Timestamp",
    CHANNEL_COLUMN: "Channel",
    CAMPAIGN_ID: "Campaign ID",
    CAMPAIGN_NAME: "Campaign Name",
    UNIQUE_ID_COLUMN: "Unique ID",
    CREATIVE_ID_COLUMN: "Creative ID",
    CLICKS: "Clicks",
    CONVERSIONS: "Conversions",
    VARIANT: "Variant",
    AGGREGATE: "Aggregate",
    GROUP_BY: "Group By",
    DATE: "Date",
    HOUR: "Hour",
    FILTER: "Filter",
    FILTER_AND_GROUP_BY: "Filter & Group By",
};

export const ValueMap = {
    allow_export_outside: { 1: "true", 0: "false" },
};

export const Descriptions = {
    audience_lookalike_modeling:
        "Train lookalike models with a seed audience against the provider (publisher)’s user base.",
    audience_overlap_analysis:
        "Instantly evaluate match rates with provider (publisher)’s user base.",
    conversion_tracking:
        "Instantly evaluate match rates with provider (publisher)’s user base.",
    inventory_forecasting_and_planning:
        "Predict ad requests generated by channel over the next 30 days.",
    reach_and_frequency:
        "Evaluate reach and frequency distribution across provider’s ad exposure data.",
    fraud_transaction_modeling:
        "Build and train consumer fraud models from multi-party credit card transaction data.",
    clinical_patient_overlap:
        "Identify common patients to with your partner health institutions to unlock joint patient opportunities.",
    data_analysis:
        "Query the provider(s) tables in the clean room without accessing the underlying data.",
    overlap_analysis:
        "Join against and analyze data in the clean room in a privacy safe fashion.",
    credit_risk_modeling: "",
};

export const groupBy = (arr, key, defaultCat) => {
    if (!arr.length) {
        return {};
    }
    return arr.reduce((res, item) => {
        const cat = item[key] || defaultCat;
        if (!res[cat]) {
            res[cat] = [];
        }
        res[cat].push(item);
        return res;
    }, {});
};

export const extractColumnNameBasedOnFqtn = ({ fqtc, fqtn }) => {
    // sample fqtc is: SAMOOHA_PROVIDER_SAMPLE_DATABASE.AUDIENCE_OVERLAP.CUSTOMERS.PHONE_UID
    // sample fqtn is: SAMOOHA_PROVIDER_SAMPLE_DATABASE.AUDIENCE_OVERLAP.CUSTOMERS

    const fqtcArr = parseFQTNC(fqtc);
    // const fqtcArr = fqtc.split(".");
    const extractedFqtnFromFqtc = `${fqtcArr?.[0]}.${fqtcArr?.[1]}.${fqtcArr?.[2]}`;

    if (fqtn === extractedFqtnFromFqtc) {
        const columnName = fqtcArr[3];
        return columnName;
    } else return "";
};

export const extractDetailsFromFqtnOrFqtc = (fqtn) => {
    const fqtnArr = parseFQTNC(fqtn);
    // const fqtnArr = fqtn?.split(".");

    return {
        databaseName: fqtnArr?.[0],
        tableName: `${fqtnArr?.[1]}.${fqtnArr?.[2]}`,
        colName: fqtnArr?.[3],
    };
};

export const getRelationSanitized = (bool, dataTable) => {
    // choose keys or values based on the bool, as the mapping comes in the below format:
    // "MY_DB.SCHEMA.TABLE.COLUMN": "OTHER_DB.SCHEMA.TABLE.COLUMN"
    const fqtn = bool
        ? Object.keys(dataTable)?.[0]
        : Object.values(dataTable)?.[0];
    const { databaseName, tableName, colName } =
        extractDetailsFromFqtnOrFqtc(fqtn);

    return {
        type: "SNOWFLAKE",
        database_name: databaseName,
        table_name: tableName,
        column: {
            name: colName,
        },
    };
};

export const sanitizeColumnSelections = ({
    table,
    secureJoinColumns,
    selectedAnalysis,
}) => {
    const fqtn = table?.fqtn;

    const filter = [];
    const groupBy = [];
    const aggregate = [];
    const segmentation = [];

    const columnSelections = selectedAnalysis?.["column_selections"];
    const allFilterColumns = columnSelections?.["filter_columns"];
    const allGroupByColumns = columnSelections?.["group_by_filter_columns"];
    const allAggregateColumns = columnSelections?.["aggregate_columns"];
    const allSegmentationColumns = columnSelections?.["segmentation_columns"];

    if (allFilterColumns) {
        allFilterColumns?.forEach((fqtc) => {
            const columnName = extractColumnNameBasedOnFqtn({
                fqtc,
                fqtn,
            });
            if (columnName) filter.push(columnName);
        });
    }

    if (allGroupByColumns) {
        allGroupByColumns?.forEach((fqtc) => {
            const columnName = extractColumnNameBasedOnFqtn({
                fqtc,
                fqtn,
            });
            if (columnName) groupBy.push(columnName);
        });
    }

    if (allAggregateColumns) {
        allAggregateColumns?.forEach((fqtc) => {
            const columnName = extractColumnNameBasedOnFqtn({
                fqtc,
                fqtn,
            });
            if (columnName) aggregate.push(columnName);
        });
    }

    if (allSegmentationColumns) {
        allSegmentationColumns?.forEach((fqtc) => {
            const columnName = extractColumnNameBasedOnFqtn({
                fqtc,
                fqtn,
            });
            if (columnName) segmentation.push(columnName);
        });
    }

    function findTypeOfCol({ colName }) {
        if (filter.includes(colName)) return "FILTER";
        if (groupBy.includes(colName)) return "GROUP_BY";
        if (aggregate.includes(colName)) return "AGGREGATE";
        if (segmentation.includes(colName)) return "SEGMENTATION_AND_ATTRIBUTE";
    }

    const sanitizedSecureJoinColumns = [];
    secureJoinColumns?.forEach((fqtc) => {
        const colName = extractColumnNameBasedOnFqtn({ fqtc, fqtn });
        if (colName) {
            sanitizedSecureJoinColumns.push(colName);
        }
    });

    // do not show secure join columns in the list
    const columnsWithTypes = [];
    table?.column?.forEach((colName) => {
        if (!sanitizedSecureJoinColumns.includes(colName)) {
            columnsWithTypes.push({
                name: colName,
                type: findTypeOfCol({ colName }) || "",
            });
        }
    });

    /* 
        Output Schema
        [{
            name: 'STATUS',
            type: 'GROUP_BY'
        }]
    */
    return columnsWithTypes;
};
